import React from "react";
import { Rocket, Leaf, Users, ArrowRight } from "lucide-react";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import {
  Container,
  Section,
  SectionHeading,
  Card,
  Button,
  Reveal,
} from "../Components/UI";

const timeline = [
  {
    year: "2022 – 23",
    title: "Researcher, ISRO",
    text: "Worked on IoT and digital-twin research at India's space agency.",
  },
  {
    year: "2023 – 25",
    title: "Generative AI Engineer, Mercedes-Benz R&D",
    text: "Built AI systems and won the Spontan Award for innovation.",
  },
  {
    year: "2024 – now",
    title: "Path Breaker, Viksit Bharat Young Leaders Dialogue",
    text: "Ministry of Youth Affairs & Sports, Government of India.",
  },
  {
    year: "2025 – now",
    title: "Founder & CEO, DevAnanta R&D",
    text: "Building India's rare-earth-free, next-gen energy storage from waste.",
  },
];

const values = [
  {
    Icon: Rocket,
    title: "A self-reliant India",
    text: "Free our country from imported batteries by inventing here, at home.",
  },
  {
    Icon: Leaf,
    title: "A healthier planet",
    text: "Turn waste into clean power and cut pollution at the source.",
  },
  {
    Icon: Users,
    title: "Dignity & inclusion",
    text: "Build with women in STEM, gender parity, and rural communities in mind.",
  },
];

const AboutPage: React.FC = () => {
  return (
    <main className="bg-ink-950 pt-16 md:pt-20">
      <Navbar />

      <Section className="!pb-10">
        <Container>
          <Reveal>
            <SectionHeading
              eyebrow="About me"
              title="The person behind the mission"
              intro="Walking the path of science with the soul of service."
            />
          </Reveal>
        </Container>
      </Section>

      {/* STORY */}
      <Section className="!pt-0">
        <Container>
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <div className="relative flex justify-center">
                <div
                  className="absolute inset-0 m-auto h-3/4 w-3/4 rounded-full bg-brand-500/15 blur-3xl"
                  aria-hidden
                />
                <img
                  src="/myPic.jpg"
                  alt="Devyansh Agrawal"
                  loading="lazy"
                  decoding="async"
                  className="relative z-10 h-[440px] w-auto object-contain drop-shadow-2xl sm:h-[540px] lg:h-[600px]"
                />
              </div>
            </Reveal>
            <Reveal delay={120}>
              <p className="text-lg leading-relaxed text-zinc-300">
                I'm Devyansh Agrawal — a scientist and entrepreneur on a mission to
                make India energy-independent. My world is where bold science meets
                real human impact.
              </p>
              <p className="mt-4 leading-relaxed text-zinc-400">
                I began in the labs of ISRO and the engineering floors of
                Mercedes-Benz. But one question kept pulling at me:{" "}
                <em>what if the waste we throw away could power a nation?</em> That
                question became DevAnanta R&D — clean, affordable energy storage,
                invented in India.
              </p>
              <p className="mt-4 leading-relaxed text-zinc-400">
                Technology alone has never been enough for me. I've walked 180+ km
                through villages, sat with the families my work is meant to serve,
                and carried their hopes back into the lab. Every breakthrough has to
                reach a real person — and every step is for peace, nature and people.
              </p>
              <div className="mt-8">
                <Button href="/recognition">
                  See my work & recognition <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* JOURNEY */}
      <Section className="bg-ink-900">
        <Container>
          <Reveal>
            <SectionHeading eyebrow="The journey" title="From the lab to a mission" />
          </Reveal>
          <div className="mx-auto mt-14 max-w-3xl">
            {timeline.map((t, i) => (
              <Reveal key={t.title} delay={i * 100}>
                <div className="flex gap-5 pb-10 last:pb-0">
                  <div className="flex flex-col items-center">
                    <span className="h-3.5 w-3.5 rounded-full bg-brand-500 ring-4 ring-brand-500/15" />
                    {i < timeline.length - 1 && (
                      <span className="mt-1 w-px flex-1 bg-white/10" />
                    )}
                  </div>
                  <div className="-mt-1 pb-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-brand-400">
                      {t.year}
                    </span>
                    <h3 className="mt-1 font-display text-lg font-semibold text-white">
                      {t.title}
                    </h3>
                    <p className="mt-1.5 text-zinc-400">{t.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      {/* VALUES */}
      <Section>
        <Container>
          <Reveal>
            <SectionHeading eyebrow="What drives me" title="Three beliefs guide my work" />
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 90}>
                <Card className="h-full p-7">
                  <span className="grid h-12 w-12 place-items-center rounded-xl bg-brand-500/10 text-brand-400">
                    <v.Icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-semibold text-white">
                    {v.title}
                  </h3>
                  <p className="mt-2 leading-relaxed text-zinc-400">{v.text}</p>
                </Card>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      <Footer />
    </main>
  );
};

export default AboutPage;
